import { Copy, MapPin, Truck } from "lucide-react";
import { marketplaceFulfilmentMethodLabel, marketplaceFulfilmentStatusLabel } from "@gaole/shared";
import { useAuth } from "../../stores/auth";
import { toast } from "../../stores/toast";

export interface SellerCollectDetails {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  postcode?: string | null;
  state?: string | null;
  country?: string | null;
  instructions?: string | null;
  phone?: string | null;
}

function collectAddressLines(d: SellerCollectDetails): string[] {
  const cityLine = [d.postcode, d.city].filter(Boolean).join(" ");
  return [d.address1, d.address2, cityLine, d.state, d.country].filter((x): x is string => Boolean(x?.trim()));
}

/** Buyer-facing marketplace fulfilment: status, courier tracking, COD handover address, cancellation. */
export function MarketplaceOrderFulfilment({
  fulfilmentMethod,
  sellerFulfilmentStatus,
  trackingNo,
  courier,
  sellerCollectDetails,
  cancelReason,
  sellerShopName,
}: {
  fulfilmentMethod?: string;
  sellerFulfilmentStatus?: string | null;
  trackingNo?: string | null;
  courier?: string | null;
  sellerCollectDetails?: SellerCollectDetails | null;
  cancelReason?: string | null;
  sellerShopName?: string | null;
}) {
  const { config } = useAuth();
  const collectLabel = config?.marketplaceCollectLabel ?? "COD";
  const isCollect = fulfilmentMethod === "PICKUP";
  const cancelled = sellerFulfilmentStatus === "CANCELLED";
  const addressLines = sellerCollectDetails ? collectAddressLines(sellerCollectDetails) : [];

  async function copyTracking() {
    if (!trackingNo) return;
    try {
      await navigator.clipboard.writeText(trackingNo);
      toast("success", "Tracking number copied");
    } catch {
      toast("error", "Couldn't copy");
    }
  }

  return (
    <div className="space-y-3 rounded-2xl border border-white/5 bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-muted">
          {marketplaceFulfilmentMethodLabel(fulfilmentMethod, collectLabel)}
          {sellerShopName ? ` · ${sellerShopName}` : ""}
        </span>
        <span className={`font-semibold ${cancelled ? "text-red-400" : "text-foreground"}`}>
          {marketplaceFulfilmentStatusLabel(sellerFulfilmentStatus, collectLabel)}
        </span>
      </div>

      {cancelled && cancelReason ? (
        <p className="rounded-xl bg-red-500/10 p-3 text-xs text-red-300">
          Cancelled by seller: {cancelReason}. Our team will process your refund.
        </p>
      ) : null}

      {!isCollect && trackingNo ? (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-white/5 p-3">
          <div className="flex min-w-0 items-center gap-2">
            <Truck size={16} className="shrink-0 text-brand-pink" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-muted">{courier || "Courier"} tracking</p>
              <p className="truncate font-mono text-xs font-semibold text-foreground">{trackingNo}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void copyTracking()}
            className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-brand-pink hover:bg-white/5"
          >
            <Copy size={12} /> Copy
          </button>
        </div>
      ) : null}

      {isCollect && addressLines.length > 0 && !cancelled ? (
        <div className="flex gap-2 rounded-xl bg-white/5 p-3 text-xs">
          <MapPin size={16} className="shrink-0 text-brand-pink" />
          <div className="space-y-0.5">
            <p className="text-[10px] uppercase tracking-wide text-muted">{collectLabel} location</p>
            {addressLines.map((line) => (
              <p key={line} className="text-foreground">
                {line}
              </p>
            ))}
            {sellerCollectDetails?.phone ? <p className="text-muted">Tel: {sellerCollectDetails.phone}</p> : null}
            {sellerCollectDetails?.instructions ? (
              <p className="pt-1 text-muted">{sellerCollectDetails.instructions}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
